import { useTheme } from '@emotion/react';
import cn from 'classnames';
import {
  cloneElement,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type MemoExoticComponent,
  memo,
  type ReactElement,
} from 'react';

import { useActive, useHover } from '@didaskill/ui/utils';

import { ICON_SIZES } from '../icon/constants';
import { Typography } from '../typography/Typography';
import { badgeStyle, buttonStyle, getIconFills } from './Button.styles';
import { BUTTON_HUES, BUTTON_ICON_POSITIONS, BUTTON_SIZES, BUTTON_VARIANTS } from './constants';

const TYPOGRAPHY_SIZE_MAPPING = {
  [BUTTON_SIZES.SMALL]: Typography.VARIANTS.BUTTON_SMALL_REGULAR,
  [BUTTON_SIZES.MEDIUM]: Typography.VARIANTS.BUTTON_MEDIUM_REGULAR,
  [BUTTON_SIZES.LARGE]: Typography.VARIANTS.BUTTON_LARGE_REGULAR,
};

const ICON_SIZE_MAPPING = {
  [BUTTON_SIZES.SMALL]: ICON_SIZES.SMALL,
  [BUTTON_SIZES.MEDIUM]: ICON_SIZES.MEDIUM,
  [BUTTON_SIZES.LARGE]: ICON_SIZES.LARGE,
};

export type ButtonProps = Omit<HTMLProps<HTMLButtonElement>, 'size' | 'ref'> & {
  /** Button HTML type. HTML default is "submit" but we override it to default to "button". */
  type?: 'button' | 'submit' | 'reset';
  /** Button color variant. */
  variant?: BUTTON_VARIANTS;
  /** Button color variant independent from theme. Do not use variant and hue at the same time. */
  hue?: BUTTON_HUES;
  /** Button size. */
  size?: BUTTON_SIZES;
  /** Show a border instead of a filled background. */
  outline?: boolean;
  /** Force active state. Does not work if disabled. */
  isActive?: boolean;
  /** Icon from IconPark. */
  icon?: ReactElement;
  /** Icon position (left or right). */
  iconPosition?: BUTTON_ICON_POSITIONS;
  /** Badge if necessary. */
  badge?: ReactElement;
};

export const ButtonBase = forwardRef(
  (
    {
      type = 'button',
      variant = BUTTON_VARIANTS.NEUTRAL,
      hue = null,
      size = BUTTON_SIZES.MEDIUM,
      outline = false,
      isActive: forceActive = false,
      icon = null,
      iconPosition = BUTTON_ICON_POSITIONS.LEFT,
      badge,
      className = null,
      children = null,
      disabled = false,
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const theme = useTheme();

    const { isHovered, ...hoverProps } = useHover(props);
    const { isActive, ...activeProps } = useActive(props);

    const isActuallyActive = isActive || forceActive;

    const onlyIcon = !!icon && !children;

    const iconComponent =
      !!icon &&
      cloneElement(icon, {
        style: {
          marginRight: onlyIcon || iconPosition === BUTTON_ICON_POSITIONS.RIGHT ? 0 : 8,
          marginLeft: onlyIcon || iconPosition === BUTTON_ICON_POSITIONS.LEFT ? 0 : 8,
        },
        size: ICON_SIZE_MAPPING[size],
        fill:
          icon.props.fill ??
          getIconFills[hue || variant](theme, outline, isActuallyActive, isHovered, disabled),
      });

    return (
      <button
        type={type}
        {...props}
        {...hoverProps}
        {...activeProps}
        ref={ref}
        css={buttonStyle}
        className={cn(className, size, variant, hue, iconPosition, {
          outline,
          onlyIcon,
          isActive: isActuallyActive,
        })}
        disabled={disabled}
      >
        {!!icon && iconPosition === BUTTON_ICON_POSITIONS.LEFT && iconComponent}

        {!!children && <Typography variant={TYPOGRAPHY_SIZE_MAPPING[size]}>{children}</Typography>}

        {!!icon && iconPosition === BUTTON_ICON_POSITIONS.RIGHT && iconComponent}

        {!!badge && <div css={badgeStyle}>{badge}</div>}
      </button>
    );
  },
);

ButtonBase.displayName = 'Button';

export const Button: MemoExoticComponent<typeof ButtonBase> & {
  VARIANTS?: typeof BUTTON_VARIANTS;
} & { HUES?: typeof BUTTON_HUES } & { SIZES?: typeof BUTTON_SIZES } & {
  ICON_POSITIONS?: typeof BUTTON_ICON_POSITIONS;
} = memo(ButtonBase);

Button.VARIANTS = BUTTON_VARIANTS;
Button.HUES = BUTTON_HUES;
Button.SIZES = BUTTON_SIZES;
Button.ICON_POSITIONS = BUTTON_ICON_POSITIONS;

export { BUTTON_HUES, BUTTON_ICON_POSITIONS, BUTTON_SIZES, BUTTON_VARIANTS };
